import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { useTranslation } from 'react-i18next';
import apiClient from '../../services/api';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFeatureArea?: string;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose, currentFeatureArea }) => {
  const { t } = useTranslation();
  const [feedbackType, setFeedbackType] = useState<string>('');
  const [featureArea, setFeatureArea] = useState<string>('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Pre-select feature area when modal opens based on current page
  useEffect(() => {
    if (isOpen && currentFeatureArea) {
      setFeatureArea(currentFeatureArea);
    }
  }, [isOpen, currentFeatureArea]);

  if (!isOpen) return null;

  const feedbackTypes = [
    { id: '', name: t('feedback:feedbackTypes.select') },
    { id: 'general_comment', name: t('feedback:feedbackTypes.generalComment') },
    { id: 'issue', name: t('feedback:feedbackTypes.issue') },
    { id: 'enhancement', name: t('feedback:feedbackTypes.enhancement') },
  ];

  const featureAreas = [
    { id: '', name: t('feedback:featureAreas.select') },
    { id: 'health_questions', name: t('feedback:featureAreas.healthQuestions') },
    { id: 'symptom_tracker', name: t('feedback:featureAreas.symptomTracker') },
    { id: 'health_metrics', name: t('feedback:featureAreas.healthMetrics') },
    { id: 'health_records', name: t('feedback:featureAreas.healthRecords') },
    { id: 'lab_results', name: t('feedback:featureAreas.labResults') },
    { id: 'imaging_reports', name: t('feedback:featureAreas.imagingReports') },
    { id: 'medication_info', name: t('feedback:featureAreas.medicationInfo') },
    { id: 'interaction_checker', name: t('feedback:featureAreas.interactionChecker') },
    { id: 'medication_list', name: t('feedback:featureAreas.medicationList') },
    { id: 'medical_glossary', name: t('feedback:featureAreas.medicalGlossary') },
    { id: 'insurance_guide', name: t('feedback:featureAreas.insuranceGuide') },
    { id: 'ai_health_advisor', name: t('feedback:featureAreas.aiHealthAdvisor') },
  ];

  const handleSubmit = async () => {
    if (!comment.trim()) {
      setErrorMessage(t('feedback:errors.commentRequired'));
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      await apiClient.post('/feedback/submit', {
        feedback_type: feedbackType || null,
        feature_area: featureArea || null,
        comment: comment,
      });

      setSubmitStatus('success');
      setTimeout(() => {
        setFeedbackType('');
        setFeatureArea('');
        setComment('');
        setSubmitStatus('idle');
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setErrorMessage(t('feedback:errors.submitFailed'));
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFeedbackType('');
    setFeatureArea('');
    setComment('');
    setSubmitStatus('idle');
    setErrorMessage('');
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('feedback:title')}
          </h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {submitStatus === 'success' && (
          <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded-lg">
            {t('feedback:success')}
          </div>
        )}

        {submitStatus === 'error' && errorMessage && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-lg">
            {errorMessage}
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('feedback:feedbackTypeLabel')}
            <span className="text-gray-400 dark:text-gray-500 ml-1">({t('feedback:optional')})</span>
          </label>
          <select
            value={feedbackType}
            onChange={(e) => setFeedbackType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            {feedbackTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('feedback:featureAreaLabel')}
            <span className="text-gray-400 dark:text-gray-500 ml-1">({t('feedback:optional')})</span>
          </label>
          <select
            value={featureArea}
            onChange={(e) => setFeatureArea(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            {featureAreas.map((area) => (
              <option key={area.id} value={area.id}>
                {area.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('feedback:commentLabel')}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('feedback:commentPlaceholder')}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
          />
        </div>

        <div className="flex space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="secondary" onClick={handleCancel} fullWidth disabled={isSubmitting}>
            {t('common:buttons.cancel')}
          </Button>
          <Button onClick={handleSubmit} fullWidth disabled={isSubmitting}>
            {isSubmitting ? t('feedback:submitting') : t('common:buttons.ok')}
          </Button>
        </div>
      </div>
    </div>
  );
};
